/**
 * Theme preference: Auto (follows prefers-color-scheme) | Light | Dark.
 * Hex values live only in tokens.css — this module reads computed CSS vars.
 */
import { getMeta, setMeta } from '../db/jobsDb.js';

export type ThemePreference = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const META_KEY = 'theme_preference';
const media =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

let mediaBound = false;

export function isThemePreference(v: unknown): v is ThemePreference {
  return v === 'auto' || v === 'light' || v === 'dark';
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'light' || pref === 'dark') return pref;
  return media?.matches ? 'dark' : 'light';
}

function syncThemeColorMeta(): void {
  const bg = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-bg')
    .trim();
  if (!bg) return;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', bg);

  const apple = document.querySelector(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  );
  if (apple) {
    const resolved = document.documentElement.dataset.theme;
    // default = light content on light status bar area; black-translucent suits dark UI
    apple.setAttribute(
      'content',
      resolved === 'dark' ? 'black-translucent' : 'default',
    );
  }
}

export function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
  // Defer so CSS variables from [data-theme] are applied before we read them
  requestAnimationFrame(() => syncThemeColorMeta());
}

export function applyThemePreference(pref: ThemePreference): void {
  applyResolvedTheme(resolveTheme(pref));
}

async function loadPreference(): Promise<ThemePreference> {
  const raw = await getMeta(META_KEY);
  return isThemePreference(raw) ? raw : 'auto';
}

export async function getThemePreference(): Promise<ThemePreference> {
  return loadPreference();
}

export async function setThemePreference(
  pref: ThemePreference,
): Promise<void> {
  await setMeta(META_KEY, pref);
  applyThemePreference(pref);
  ensureMediaListener();
}

function onMediaChange(): void {
  void loadPreference().then((pref) => {
    if (pref === 'auto') applyThemePreference('auto');
  });
}

function ensureMediaListener(): void {
  if (!media || mediaBound) return;
  mediaBound = true;
  media.addEventListener('change', onMediaChange);
}

/** Call once at boot — before paint if possible. */
export async function initTheme(): Promise<ThemePreference> {
  const pref = await loadPreference();
  applyThemePreference(pref);
  ensureMediaListener();
  return pref;
}
